import { usePermissions } from './index';

/**
 * Custom hook for easy permission checking in components
 * @param {number|string} moduleId - The module ID to check permissions for
 * @returns {object} Permission checking functions and module permissions
 */
export const useModulePermissions = (moduleId) => {
  const permissionUtils = usePermissions();
  
  if (!moduleId) {
    return {
      ...permissionUtils,
      modulePermissions: null,
      canVisible: () => false,
      canView: () => false,
      canAdd: () => false,
      canEdit: () => false,
      canDelete: () => false,
      canPrint: () => false,
      canExport: () => false,
      canSameDateUpdate: () => false,
    };
  }

  const modulePermissions = permissionUtils.getModulePermissions(moduleId);
  return {
    ...permissionUtils,
    modulePermissions,
    // Module-specific permission checks
    canVisible: () => permissionUtils.canVisible(moduleId),
    canView: () => permissionUtils.canView(moduleId),
    canAdd: () => permissionUtils.canAdd(moduleId),
    canEdit: () => permissionUtils.canEdit(moduleId),
    canDelete: () => permissionUtils.canDelete(moduleId),
    canPrint: () => permissionUtils.canPrint(moduleId),
    canExport: () => permissionUtils.canExport(moduleId),
    canSameDateUpdate: () => permissionUtils.canSameDateUpdate(moduleId),
    // CRUD operations
    canCreate: () => permissionUtils.canAdd(moduleId),
    canRead: () => permissionUtils.canView(moduleId),
    canUpdate: () => permissionUtils.canEdit(moduleId),
    canDestroy: () => permissionUtils.canDelete(moduleId),
    // Check if user has any access to this module
    hasAccess: () => permissionUtils.hasAnyPermission(moduleId),
  };
};

/**
 * Hook for checking specific permissions across multiple modules
 * @param {Array} moduleIds - Array of module IDs to check
 * @returns {object} Permission checking functions for multiple modules
 */
export const useMultiModulePermissions = (moduleIds = []) => {
  const permissionUtils = usePermissions();
  
  const checkAllModules = (permissionType) => {
    return moduleIds.every(moduleId => permissionUtils.hasPermission(moduleId, permissionType));
  };
  
  const checkAnyModule = (permissionType) => {
    return moduleIds.some(moduleId => permissionUtils.hasPermission(moduleId, permissionType));
  };
  
  const getModulePermissionsMap = () => {
    const permissionsMap = {};
    moduleIds.forEach(moduleId => {
      permissionsMap[moduleId] = permissionUtils.getModulePermissions(moduleId);
    });
    return permissionsMap;
  };
  
  return {
    ...permissionUtils,
    moduleIds,
    modulePermissionsMap: getModulePermissionsMap(),
    // Check if user has permission for ALL modules
    canViewAll: () => checkAllModules('view'),
    canAddAll: () => checkAllModules('add'),
    canEditAll: () => checkAllModules('edit'),
    canDeleteAll: () => checkAllModules('delete'),
    canPrintAll: () => checkAllModules('print'),
    canExportAll: () => checkAllModules('export'),
    // Check if user has permission for ANY module
    canViewAny: () => checkAnyModule('view'),
    canAddAny: () => checkAnyModule('add'),
    canEditAny: () => checkAnyModule('edit'),
    canDeleteAny: () => checkAnyModule('delete'),
    canPrintAny: () => checkAnyModule('print'),
    canExportAny: () => checkAnyModule('export'),
    // Generic permission checks
    hasAllPermissions: (permissionType) => checkAllModules(permissionType),
    hasAnyPermission: (permissionType) => checkAnyModule(permissionType),
  };
};

/**
 * Hook for permission-based conditional rendering
 * @param {number|string} moduleId - The module ID
 * @param {string} permissionType - The permission type to check
 * @returns {boolean} Whether the user has the specified permission
 */
export const usePermissionGuard = (moduleId, permissionType) => {
  const permissionUtils = usePermissions();
  return permissionUtils.hasPermission(moduleId, permissionType);
};

/**
 * Hook for getting all accessible modules for the current user
 * @returns {Array} Array of modules the user has access to
 */
export const useAccessibleModules = () => {
  const permissionUtils = usePermissions();
  return permissionUtils.getAccessibleModules();
};

/**
 * Hook for getting modules with specific permissions
 * @param {string} permissionType - The permission type to filter by
 * @returns {Array} Array of modules with the specified permission
 */
export const useModulesWithPermission = (permissionType) => {
  const permissionUtils = usePermissions();
  return permissionUtils.getModulesWithPermission(permissionType);
}; 